import { CollectionMetadata } from "@oysterhr/schema"
import {
  UseTableSelectQueryParamStateArgs,
  UseTableSelectQueryParamStateController,
  useTableSelectQueryParamState,
} from "./useTableSelectQueryParamsState"
import { useMemo } from "react"
import { Identifiable } from "./types"

export interface UseTableSelectionControllerArgs
  extends Omit<UseTableSelectQueryParamStateArgs, "displayedIds"> {
  /**
   * This is the metdata of the fetched collection
   * @example
   * data.engagements.metadata
   */
  metadata?: CollectionMetadata
  /**
   * This is the collection of entries displayed in the table.
   * IMPORTANT: Every entry should have the ID defined.
   * @example
   * data.engagements.collection
   */
  collection?: Identifiable[]
}

export interface TableSelectionController extends UseTableSelectQueryParamStateController {
  selectedEntriesCount: number
}

export function useTableSelectionController({
  metadata,
  queryParamName,
  resetOnQueryParamsChange,
  collection,
}: UseTableSelectionControllerArgs): TableSelectionController {
  const displayedIds = useMemo(() => collection?.map(({ id }) => id) ?? [], [collection])

  const tableSelectController = useTableSelectQueryParamState({
    resetOnQueryParamsChange,
    queryParamName,
    displayedIds,
  })

  const { selectedIds } = tableSelectController

  const selectedEntriesCount = useMemo(() => {
    if (selectedIds === "ALL_RECORDS") return metadata?.totalCount ?? Infinity

    return selectedIds.length
  }, [metadata, selectedIds])

  return {
    ...tableSelectController,
    selectedEntriesCount,
  }
}
